/**
 * Voice notes. Uses the browser's MediaRecorder, which also works inside the
 * Capacitor web view (Android WebView; WKWebView from iOS 14.3). The native
 * projects declare the microphone permission; the web view asks the user.
 *
 * Recording is entirely on the device, so it works with no signal.
 */
import { ALLOWED_AUDIO_TYPES } from '@klunkers/shared'

export type AudioType = (typeof ALLOWED_AUDIO_TYPES)[number]

/** Longest note the app records. The API accepts a little more. */
export const MAX_RECORDING_SECONDS = 60

export interface Recording {
  blob: Blob
  contentType: AudioType
  durationS: number
}

export interface Recorder {
  /** Stop and return the recording (null if nothing was captured). */
  stop(): Promise<Recording | null>
  /** Stop and throw the recording away. */
  cancel(): void
}

// Preferred first. Chrome and Android record WebM/Opus; Safari records MP4/AAC.
const CANDIDATES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/aac', 'audio/ogg']

export function canRecord(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.MediaRecorder !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia
  )
}

/** The container a recorder produced, reduced to a type the API accepts. */
export function audioType(mime: string): AudioType {
  const base = mime.split(';')[0]!.trim().toLowerCase()
  if (base === 'audio/x-m4a' || base === 'audio/m4a') return 'audio/mp4'
  return (ALLOWED_AUDIO_TYPES as readonly string[]).includes(base)
    ? (base as AudioType)
    : 'audio/webm'
}

export class MicrophoneError extends Error {
  constructor(message = 'Klunkers needs the microphone to record a voice note.') {
    super(message)
    this.name = 'MicrophoneError'
  }
}

/**
 * Start recording. Stops by itself at `maxSeconds`, calling `onLimit`.
 * Throws MicrophoneError if the microphone is unavailable or refused.
 */
export async function startRecording(
  opts: { maxSeconds?: number; onLimit?: () => void } = {},
): Promise<Recorder> {
  if (!canRecord()) throw new MicrophoneError('This device cannot record voice notes.')
  let stream: MediaStream
  try {
    stream = await navigator.mediaDevices.getUserMedia({ audio: true })
  } catch {
    throw new MicrophoneError()
  }
  const mimeType = CANDIDATES.find((t) => MediaRecorder.isTypeSupported(t))
  const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined)
  const chunks: Blob[] = []
  const started = performance.now()
  recorder.ondataavailable = (e) => {
    if (e.data.size) chunks.push(e.data)
  }
  const release = () => stream.getTracks().forEach((t) => t.stop())
  const done = new Promise<void>((resolve) => {
    recorder.onstop = () => {
      release()
      resolve()
    }
  })
  recorder.start(1000)
  const limit = setTimeout(
    () => {
      if (recorder.state !== 'inactive') opts.onLimit?.()
    },
    (opts.maxSeconds ?? MAX_RECORDING_SECONDS) * 1000,
  )

  return {
    async stop() {
      clearTimeout(limit)
      const durationS = Math.round((performance.now() - started) / 100) / 10
      if (recorder.state !== 'inactive') recorder.stop()
      await done
      if (!chunks.length) return null
      const type = audioType(recorder.mimeType || mimeType || 'audio/webm')
      return { blob: new Blob(chunks, { type }), contentType: type, durationS }
    },
    cancel() {
      clearTimeout(limit)
      chunks.length = 0
      if (recorder.state !== 'inactive') recorder.stop()
      else release()
    },
  }
}

/** m:ss for a length in seconds. */
export function formatDuration(seconds: number): string {
  const s = Math.max(0, Math.round(seconds))
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}
